/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { DatabaseBackup, Download, Upload, ShieldCheck, Clock, RefreshCw, HardDrive, Lock, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { getBackupStatus, putBackupSchedule, exportBackup, restoreBackup, runBackupNow } from '@/lib/enterprise';

/**
 * BackupPage — Disaster Recovery locale (GDPR art. 32): export cifrato AES-256-GCM
 * scaricabile, ripristino con passphrase, backup programmato su disco.
 */
export default function BackupPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pass, setPass] = useState('');
  const [pass2, setPass2] = useState('');
  const [exporting, setExporting] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restorePass, setRestorePass] = useState('');
  const [restoreMode, setRestoreMode] = useState('upsert');
  const [restoring, setRestoring] = useState(false);
  const [schedule, setSchedule] = useState({ enabled: false, hour_utc: 2, retention_days: 30 });
  const [savingSchedule, setSavingSchedule] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = await getBackupStatus();
      setStatus(s);
      setSchedule(s.schedule);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Stato backup non disponibile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doExport = async () => {
    if (pass.length < 12) { toast.error('Passphrase di almeno 12 caratteri'); return; }
    if (pass !== pass2) { toast.error('Le passphrase non coincidono'); return; }
    setExporting(true);
    try {
      const res = await exportBackup(pass);
      const cd = res.headers?.['content-disposition'] || '';
      const name = (cd.match(/filename="([^"]+)"/) || [])[1] || `sanitanexus-backup-${Date.now()}.snbak`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success(`Backup cifrato scaricato (${res.headers?.['x-backup-documents'] || '?'} documenti)`);
      setPass(''); setPass2('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export non riuscito');
    } finally {
      setExporting(false);
    }
  };

  const doRestore = async () => {
    if (!restoreFile || !restorePass) { toast.error('Seleziona il file e inserisci la passphrase'); return; }
    if (restoreMode === 'replace' && !window.confirm('ATTENZIONE: la modalità "sostituisci" svuota le collezioni prima del ripristino. Continuare?')) return;
    setRestoring(true);
    try {
      const r = await restoreBackup(restoreFile, restorePass, restoreMode);
      toast.success(`Ripristinati ${r.documents} documenti`);
      setRestoreFile(null); setRestorePass('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ripristino non riuscito');
    } finally {
      setRestoring(false);
    }
  };

  const saveSchedule = async () => {
    setSavingSchedule(true);
    try {
      await putBackupSchedule(schedule);
      toast.success('Pianificazione salvata');
      load();
    } catch (e) { toast.error('Salvataggio non riuscito'); } finally { setSavingSchedule(false); }
  };

  const [runningNow, setRunningNow] = useState(false);
  const runNow = async () => {
    if (runningNow) return;
    setRunningNow(true);
    try {
      const r = await runBackupNow();
      toast.success(`Backup su disco creato: ${r.filename}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backup su disco non eseguito');
    } finally { setRunningNow(false); }
  };

  return (
    <div className="space-y-5 max-w-5xl" data-testid="backup-page">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight flex items-center gap-2"><DatabaseBackup className="h-6 w-6 text-primary" /> Disaster Recovery</h1>
        <p className="text-sm text-muted-foreground mt-1">Dump completo del database (MongoDB o PostgreSQL/Supabase) compresso e cifrato AES-256-GCM. La passphrase non viene mai salvata.</p>
      </div>

      {loading && !status ? <Skeleton className="h-20 rounded-xl" /> : status && (
        <div className="grid gap-3 grid-cols-2 md:grid-cols-4" data-testid="backup-status">
          <Card className="rounded-xl"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Documenti</div><div className="font-display font-bold text-xl" data-testid="backup-total-docs">{status.total_documents.toLocaleString('it-IT')}</div></CardContent></Card>
          <Card className="rounded-xl"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Collezioni</div><div className="font-display font-bold text-xl">{Object.keys(status.collections).length}</div></CardContent></Card>
          <Card className="rounded-xl"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Ultimo backup</div><div className="font-display font-bold text-sm">{status.last_run ? new Date(status.last_run.at).toLocaleString('it-IT') : '—'}</div><div className="text-[11px] text-muted-foreground">{status.last_run?.kind}</div></CardContent></Card>
          <Card className="rounded-xl"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Cifratura</div><div className="font-semibold text-xs flex items-center gap-1"><Lock className="h-3.5 w-3.5 text-primary" /> AES-256-GCM</div><div className="text-[11px] text-muted-foreground">PBKDF2 310k</div></CardContent></Card>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Export */}
        <Card className="rounded-xl" data-testid="backup-export-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-display flex items-center gap-2"><Download className="h-4 w-4 text-primary" /> Esporta backup cifrato</CardTitle>
            <CardDescription>Scarica un file <span className="font-mono">.snbak</span> da conservare su supporto offline (GDPR art. 32).</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label>Passphrase (min. 12 caratteri)</Label>
              <Input type="password" value={pass} onChange={(e) => setPass(e.target.value)} className="h-11 rounded-xl" autoComplete="new-password" data-testid="backup-passphrase" />
            </div>
            <div>
              <Label>Conferma passphrase</Label>
              <Input type="password" value={pass2} onChange={(e) => setPass2(e.target.value)} className="h-11 rounded-xl" autoComplete="new-password" data-testid="backup-passphrase-confirm" />
            </div>
            <Alert className="rounded-xl">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle className="text-sm">Conserva la passphrase</AlertTitle>
              <AlertDescription className="text-xs">Senza passphrase il backup è irrecuperabile: nessuno (nemmeno il fornitore) può decifrarlo.</AlertDescription>
            </Alert>
            <Button onClick={doExport} disabled={exporting} className="w-full h-11 rounded-xl" data-testid="backup-export-btn">
              {exporting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} {exporting ? 'Cifratura in corso…' : 'Scarica backup cifrato'}
            </Button>
          </CardContent>
        </Card>

        {/* Restore */}
        <Card className="rounded-xl" data-testid="backup-restore-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-display flex items-center gap-2"><Upload className="h-4 w-4 text-primary" /> Ripristina da backup</CardTitle>
            <CardDescription>Carica un file <span className="font-mono">.snbak</span> e la relativa passphrase.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label>File backup</Label>
              <Input type="file" accept=".snbak" onChange={(e) => setRestoreFile(e.target.files?.[0] || null)} className="h-11 rounded-xl pt-2" data-testid="backup-restore-file" />
            </div>
            <div>
              <Label>Passphrase</Label>
              <Input type="password" value={restorePass} onChange={(e) => setRestorePass(e.target.value)} className="h-11 rounded-xl" data-testid="backup-restore-passphrase" />
            </div>
            <div>
              <Label>Modalità</Label>
              <Select value={restoreMode} onValueChange={setRestoreMode}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="backup-restore-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="upsert">Unisci (upsert, sicuro)</SelectItem>
                  <SelectItem value="replace">Sostituisci (svuota e ricarica)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={doRestore} disabled={restoring || !restoreFile} className="w-full h-11 rounded-xl" data-testid="backup-restore-btn">
              {restoring ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {restoring ? 'Ripristino…' : 'Ripristina'}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Schedule */}
      <Card className="rounded-xl" data-testid="backup-schedule-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <CardTitle className="text-base font-display flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> Backup programmato su disco</CardTitle>
              <CardDescription>Ogni giorno all'ora indicata (UTC) in <span className="font-mono">{status?.backup_dir || './backups'}</span>. Richiede <span className="font-mono">BACKUP_PASSPHRASE</span> nel .env del server.</CardDescription>
            </div>
            {status && (status.scheduler_passphrase_configured
              ? <Badge variant="success" className="text-[10px]"><CheckCircle2 className="h-3 w-3 mr-1" /> passphrase server configurata</Badge>
              : <Badge variant="outline" className="text-[10px] border-warning/50 text-warning"><AlertTriangle className="h-3 w-3 mr-1" /> BACKUP_PASSPHRASE assente</Badge>)}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-[auto_1fr_1fr_auto] items-end">
            <label className="flex items-center gap-2 h-11">
              <Switch checked={schedule.enabled} onCheckedChange={(v) => setSchedule({ ...schedule, enabled: v })} data-testid="backup-schedule-enabled" />
              <span className="text-sm">Attivo</span>
            </label>
            <div>
              <Label className="text-xs">Ora (UTC)</Label>
              <Input type="number" min={0} max={23} value={schedule.hour_utc} onChange={(e) => setSchedule({ ...schedule, hour_utc: Number(e.target.value) })} className="h-11 rounded-xl" data-testid="backup-schedule-hour" />
            </div>
            <div>
              <Label className="text-xs">Conservazione (giorni)</Label>
              <Input type="number" min={1} max={3650} value={schedule.retention_days} onChange={(e) => setSchedule({ ...schedule, retention_days: Number(e.target.value) })} className="h-11 rounded-xl" data-testid="backup-schedule-retention" />
            </div>
            <div className="flex gap-2">
              <Button onClick={saveSchedule} disabled={savingSchedule} className="h-11 rounded-xl" data-testid="backup-schedule-save">Salva</Button>
              <Button variant="outline" onClick={runNow} disabled={runningNow || savingSchedule} className="h-11 rounded-xl" data-testid="backup-run-now">{runningNow ? <RefreshCw className="h-4 w-4 animate-spin" /> : <HardDrive className="h-4 w-4" />} {runningNow ? 'In corso…' : 'Esegui ora'}</Button>
            </div>
          </div>
          {status?.files?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2">File su disco ({status.files.length})</div>
              <ul className="text-xs space-y-1 font-mono">
                {status.files.map((f) => <li key={f.name} className="flex justify-between gap-3 rounded-lg bg-muted/40 px-3 py-1.5"><span className="truncate">{f.name}</span><span className="text-muted-foreground whitespace-nowrap">{(f.size_bytes / 1024 / 1024).toFixed(2)} MB</span></li>)}
              </ul>
            </div>
          )}
          <p className="text-[11px] text-muted-foreground flex items-start gap-1.5"><ShieldCheck className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /> Conforme al principio di integrità e riservatezza (GDPR art. 5 e 32): i dump non contengono mai dati in chiaro.</p>
        </CardContent>
      </Card>
    </div>
  );
}
